import * as React from "react";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { EmailInput } from "./EmailInput";
import { PasswordInput } from "./PasswordInput";
import { RememberMeCheckbox } from "./RememberMeCheckbox";
import { FormError } from "./FormError";
import { toast } from "sonner";

interface LoginFormProps {
  onSuccess?: () => void;
}

/**
 * Login Form Component
 *
 * Email/password login form with validation.
 */
export function LoginForm({ onSuccess }: LoginFormProps) {
  const { signIn, loading } = useAuth();
  const { t } = useTranslation("auth");

  const isLocalDev =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

  const [email, setEmail] = useState(isLocalDev ? "dev@example.com" : "");
  const [password, setPassword] = useState(isLocalDev ? "123123123" : "");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email) {
      newErrors.email = t("validation.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t("validation.emailInvalid");
    }

    if (!password) {
      newErrors.password = t("validation.passwordRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    try {
      await signIn(email, password, rememberMe);
      toast.success(t("login.successToast"));
      onSuccess?.();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to sign in. Please try again.";
      setError(message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormError message={error} />

      <EmailInput
        label={t("login.emailLabel")}
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setErrors((prev) => ({ ...prev, email: undefined }));
        }}
        placeholder={t("login.emailPlaceholder")}
        error={errors.email}
        disabled={loading}
      />

      <PasswordInput
        label={t("login.passwordLabel")}
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          setErrors((prev) => ({ ...prev, password: undefined }));
        }}
        placeholder={t("login.passwordPlaceholder")}
        error={errors.password}
        disabled={loading}
      />

      <div className="flex items-center justify-between">
        <RememberMeCheckbox
          checked={rememberMe}
          onCheckedChange={setRememberMe}
          label={t("login.rememberMe")}
        />
        <Link to="/forgot-password" className="text-sm text-primary hover:underline">
          {t("login.forgotPassword")}
        </Link>
      </div>

      <Button type="submit" className="h-11 w-full" disabled={loading}>
        {loading ? (
          <>
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
            {t("login.submitting")}
          </>
        ) : (
          t("login.submit")
        )}
      </Button>
    </form>
  );
}

export default LoginForm;
